import { FC, useRef, useState, useEffect } from 'react';
import {
    Box, Button, Typography, Grid, Card, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, IconButton, Chip, Tooltip
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ocrScanInvoice, processPurchaseInvoice, OcrScanResponse } from '../../store/invoiceStore/ocrThunk';
import { fetchStates } from '../../store/stateStore/stateThunk';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { IOptions } from '../../components/FormFields/CustomSelect';
import { Autocomplete } from '@mui/material';
import '../../components/Invoice/Invoice.css';
import './ScanInvoice.css';

const ScanInvoice: FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { selectedAccount } = useAppSelector(state => state.auth);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [scanResult, setScanResult] = useState<OcrScanResponse | null>(null);
    const [previewImage, setPreviewImage] = useState<string>('');
    const [dragActive, setDragActive] = useState(false);
    const [stateOptions, setStateOptions] = useState<IOptions[]>([]);

    // Form state
    const [invoiceSerialNumber, setInvoiceSerialNumber] = useState('');
    const [deliveryNumber, setDeliveryNumber] = useState('');
    const [invoiceDate, setInvoiceDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [poNumber, setPoNumber] = useState('');
    const [placeOfSupply, setPlaceOfSupply] = useState<any>(null);
    const [isTaxTypeIgst, setIsTaxTypeIgst] = useState(false);
    const [notes, setNotes] = useState('');
    const [bankName, setBankName] = useState('');
    const [bankAccount, setBankAccount] = useState('');
    const [bankIfsc, setBankIfsc] = useState('');
    const [scannedFile, setScannedFile] = useState<File | null>(null);
    const [additionalCharges, setAdditionalCharges] = useState<any[]>([]);
    const [lineItems, setLineItems] = useState<any[]>([]);
    const [taxableAmount, setTaxableAmount] = useState(0);
    const [cgstAmount, setCgstAmount] = useState(0);
    const [sgstAmount, setSgstAmount] = useState(0);
    const [igstAmount, setIgstAmount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);

    useEffect(() => {
        dispatch(fetchStates()).unwrap().then((data: any) => {
            if (data?.length > 0) setStateOptions(data.map((item: any) => ({ label: item.name, value: item._id })));
        });
    }, [dispatch]);

    const normalizeDateForInput = (value: string) => {
        if (!value) return '';
        const trimmed = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
        const match = trimmed.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})$/);
        if (!match) return '';
        const day = match[1].padStart(2, '0');
        const month = match[2].padStart(2, '0');
        const year = match[3].length === 2 ? `20${match[3]}` : match[3];
        return `${year}-${month}-${day}`;
    };
    const populateFormFromScan = (result: OcrScanResponse) => {
        const d = result.extractedData;
        setInvoiceSerialNumber(d.invoiceNumber || '');
        setDeliveryNumber((d as any).deliveryNumber || '');
        setInvoiceDate(normalizeDateForInput(d.invoiceDate || '')); 
        setDueDate(normalizeDateForInput(d.dueDate || '')); 
        setPoNumber(d.poNumber || '');
        setNotes(d.notes || '');
        setIsTaxTypeIgst(d.isTaxTypeIgst || false);
        setTaxableAmount(d.taxableAmount || 0);
        setCgstAmount(d.cgstAmount || 0);
        setSgstAmount(d.sgstAmount || 0);
        setIgstAmount(d.igstAmount || 0);
        setTotalAmount(d.totalAmount || 0);
        setBankName(d.bankDetails?.bankName || '');
        setBankAccount(d.bankDetails?.accountNumber || '');
        setBankIfsc(d.bankDetails?.ifsc || '');
        setAdditionalCharges((d as any).additionalCharges || []);
        if (d.placeOfSupply && stateOptions.length > 0) {
            const matched = stateOptions.find((s: any) => s.label.toLowerCase().includes(d.placeOfSupply.toLowerCase()) || d.placeOfSupply.toLowerCase().includes(s.label.toLowerCase()));
            if (matched) setPlaceOfSupply(matched);
        }
        const items = result.matchedItems.map((item: any, idx: number) => ({
            id: idx + 1, name: item.name || '', description: item.description || '', hsnCode: item.hsnCode || '',
            qty: item.qty || 1, rate: item.rate || 0, discount: item.discount || 0,
            taxableValue: item.taxableValue || 0, taxPercent: item.taxPercent || 0, total: item.total || 0, matchedSku: item.matchedSku,
        }));
        setLineItems(items);
        // Recalculate totals from line items to get accurate decimal values
        // Use OCR extracted totals directly - they already include additional charges
        // Only recalculate if OCR totals are missing
        if (!d.totalAmount) {
            const _isTaxTypeIgst = d.isTaxTypeIgst || false;
            let tTaxable = 0, tCgst = 0, tSgst = 0, tIgst = 0, tTotal = 0;
            items.forEach((li: any) => { const tv = Number(li.taxableValue) || 0; const tax = (tv * (Number(li.taxPercent) || 0)) / 100;
                tTaxable += tv; if (_isTaxTypeIgst) tIgst += tax; else { tCgst += tax / 2; tSgst += tax / 2; } tTotal += tv + tax; });
            const addChargesSum = ((d as any).additionalCharges || []).reduce((sum: number, c: any) => sum + (Number(c.value) || 0), 0);
            tTotal += addChargesSum;
            setTaxableAmount(tTaxable); setCgstAmount(tCgst); setSgstAmount(tSgst); setIgstAmount(tIgst); setTotalAmount(tTotal);
        }
    };

    const handleFileProcess = async (file: File) => {
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];
        if (!validTypes.includes(file.type)) { toast.error('Please upload a valid image or PDF'); return; }
        if (file.size > 10 * 1024 * 1024) { toast.error('File size should be less than 10MB'); return; }
        const reader = new FileReader();
        reader.onload = (e) => setPreviewImage(e.target?.result as string);
        reader.readAsDataURL(file);
        setScannedFile(file);
        setIsScanning(true);
        try {
            const result = await dispatch(ocrScanInvoice({ file, accountId: selectedAccount._id })).unwrap();
            setScanResult(result);
            populateFormFromScan(result);
            toast.success('Invoice scanned successfully!');
        } catch (error: any) { toast.error(error?.message || 'Failed to scan invoice'); }
        finally { setIsScanning(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.files?.[0]) handleFileProcess(e.target.files[0]); };
    const handleDrag = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
    const handleDrop = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleFileProcess(e.dataTransfer.files[0]); };

    const updateLineItem = (index: number, field: string, value: any) => {
        const updated = [...lineItems]; updated[index] = { ...updated[index], [field]: value };
        // If name or hsnCode changed by user, clear matchedSku so backend will re-check and create if needed
        if (field === 'name' || field === 'hsnCode') {
            updated[index].matchedSku = null;
        }
        const item = updated[index];
        const taxableValue = ((Number(item.qty) || 0) * (Number(item.rate) || 0)) - (Number(item.discount) || 0);
        updated[index].taxableValue = taxableValue;
        updated[index].total = taxableValue + (taxableValue * (Number(item.taxPercent) || 0)) / 100;
        setLineItems(updated); recalcTotals(updated);
    };
    const recalcTotals = (items: any[]) => {
        let tTaxable = 0, tCgst = 0, tSgst = 0, tIgst = 0, tTotal = 0;
        items.forEach(li => { const tv = Number(li.taxableValue) || 0; const tax = (tv * (Number(li.taxPercent) || 0)) / 100;
            tTaxable += tv; if (isTaxTypeIgst) tIgst += tax; else { tCgst += tax / 2; tSgst += tax / 2; } tTotal += tv + tax; });
        // Add additional charges to total
        const addChargesSum = additionalCharges.reduce((sum: number, c: any) => sum + (Number(c.value) || 0), 0);
        tTotal += addChargesSum;
        setTaxableAmount(tTaxable); setCgstAmount(tCgst); setSgstAmount(tSgst); setIgstAmount(tIgst); setTotalAmount(tTotal);
    };
    const removeLineItem = (i: number) => { const u = lineItems.filter((_, idx) => idx !== i); setLineItems(u); recalcTotals(u); };
    const addLineItem = () => { setLineItems([...lineItems, { id: lineItems.length + 1, name: '', description: '', hsnCode: '', qty: 1, rate: 0, discount: 0, taxableValue: 0, taxPercent: 0, total: 0, matchedSku: null }]); };

    const getPartyAddress = () => {
        const d = scanResult?.extractedData as any;
        if (!d) return '';
        if (typeof d.supplierAddress === 'object') {
            return [d.supplierAddress.line1, d.supplierAddress.line2, d.supplierAddress.city, d.supplierAddress.state, d.supplierAddress.pincode].filter(Boolean).join(', ');
        }
        return d.supplierAddress || '';
    };
    const getSupplierAddress = () => {
        const d = scanResult?.extractedData as any;
        if (!d) return '';
        if (typeof d.supplierAddress === 'object') {
            return [d.supplierAddress.line1, d.supplierAddress.line2, d.supplierAddress.city, d.supplierAddress.state, d.supplierAddress.pincode].filter(Boolean).join(', ');
        }
        return d.supplierAddress || '';
    };
    const getBillToAddress = () => {
        const d = scanResult?.extractedData as any;
        if (!d) return '';
        if (typeof d.billToAddress === 'object') {
            return [d.billToAddress.line1, d.billToAddress.line2, d.billToAddress.city, d.billToAddress.state, d.billToAddress.pincode].filter(Boolean).join(', ');
        }
        return d.billToAddress || '';
    };
    const getShipToAddress = () => {
        const d = scanResult?.extractedData as any;
        if (!d) return '';
        if (typeof d.shipToAddress === 'object') {
            return [d.shipToAddress.line1, d.shipToAddress.line2, d.shipToAddress.city, d.shipToAddress.state, d.shipToAddress.pincode].filter(Boolean).join(', ');
        }
        return d.shipToAddress || d.billToAddress || '';
    };

    const handleSave = async (status: string = 'PENDING') => {
        if (!scanResult?.extractedData?.billToName && !scanResult?.extractedData?.supplierName) { toast.error('Party name is required'); return; }
        if (lineItems.length === 0) { toast.error('At least one line item is required'); return; }
        setIsSaving(true);
        try {
            const d = scanResult?.extractedData as any;
            const payload = {
                accountId: selectedAccount._id, status,
                supplierName: d.supplierName || d.billToName, supplierLegalName: d.supplierLegalName || d.supplierName,
                supplierGST: d.supplierGST, supplierPAN: d.supplierPAN,
                supplierPhone: d.supplierPhone || '', supplierEmail: d.supplierEmail || '',
                supplierAddress: getSupplierAddress(),
                supplierAddressLine1: typeof d.supplierAddress === 'object' ? d.supplierAddress.line1 : (d.supplierAddress || ''),
                supplierAddressLine2: typeof d.supplierAddress === 'object' ? d.supplierAddress.line2 : '',
                supplierCity: typeof d.supplierAddress === 'object' ? d.supplierAddress.city : '',
                supplierPincode: typeof d.supplierAddress === 'object' ? d.supplierAddress.pincode : '',
                supplierState: typeof d.supplierAddress === 'object' ? d.supplierAddress.state : '',
                supplierStateCode: typeof d.supplierAddress === 'object' ? d.supplierAddress.stateCode : '',
                refInvoiceNumber: invoiceSerialNumber, deliveryNumber,
                billToAddress: getBillToAddress(), shipToAddress: getShipToAddress(),
                invoiceDate, dueDate, poNumber, placeOfSupply: placeOfSupply?.value || '',
                notes, isTaxTypeIgst, taxableAmount, cgstAmount, sgstAmount, igstAmount, totalAmount,
                bankName, bankAccount, bankIfsc,
                additionalCharges: additionalCharges.filter(c => c.title && c.value > 0),
                lineItems: lineItems.map(item => ({
                    name: item.name, description: item.description, hsnCode: item.hsnCode,
                    qty: Number(item.qty) || 1, rate: Number(item.rate) || 0, discount: Number(item.discount) || 0, discountPercentage: 0,
                    taxableValue: Number(item.taxableValue) || 0, taxPercent: Number(item.taxPercent) || 0,
                    cgst: !isTaxTypeIgst ? (Number(item.taxPercent) || 0) / 2 : 0,
                    cgsttotal: !isTaxTypeIgst ? ((Number(item.taxableValue) || 0) * (Number(item.taxPercent) || 0) / 200) : 0,
                    sgst: !isTaxTypeIgst ? (Number(item.taxPercent) || 0) / 2 : 0,
                    sgsttotal: !isTaxTypeIgst ? ((Number(item.taxableValue) || 0) * (Number(item.taxPercent) || 0) / 200) : 0,
                    igst: isTaxTypeIgst ? (Number(item.taxPercent) || 0) : 0,
                    igsttotal: isTaxTypeIgst ? ((Number(item.taxableValue) || 0) * (Number(item.taxPercent) || 0) / 100) : 0,
                    taxPercentage: Number(item.taxPercent) || 0,
                    total: Number(item.total) || 0, skuId: item.matchedSku?._id || '',
                })),
            };
            const result = await dispatch(processPurchaseInvoice(payload)).unwrap();
            if (scannedFile && result?.invoice?._id) {
                try {
                    const formData = new FormData();
                    formData.append('attachment', scannedFile);
                    formData.append('collectionName', 'Invoice');
                    formData.append('collectionRefId', result.invoice._id);
                    formData.append('documentName', 'Scanned Invoice');
                    const axios = (await import('../../utils/axios')).default;
                    await axios.post('/api/v1/attachments?fieldName=Scanned+Invoice', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                } catch (attachErr) { console.log('Attachment upload failed:', attachErr); }
            }
            toast.success('Purchase Invoice created successfully!');
            if (result?.invoice?._id) navigate(`/invoice/${result.invoice._id}/preview?templateId=${result.invoice.pdfTemplate || ''}&type=purchase`);
            else navigate('/purchases');
        } catch (error: any) { toast.error(error?.message || 'Failed to create invoice'); }
        finally { setIsSaving(false); }
    };

    const d = scanResult?.extractedData as any;

    return (
        <>
            {/* ==================== UPLOAD ==================== */}
            {!scanResult && (
                <Card variant="outlined" className="scan-form-card" style={{ height: 'fit-content', border: '1px solid #d0d0d0', borderRadius: '8px', boxShadow: 'none' }}>
                    <Box sx={{ border: '3px solid #fff', backgroundColor: '#fff' }}>
                        <Grid container>
                            <Grid {...{ xs: 12, p: 2, pl: 1.5, pt: 1.6 }} style={{ height: 50 }}>
                                <IconButton style={{ float: 'left', color: '#488dff', backgroundColor: '#e3f2fd', border: 'none', height: 35, width: 35, marginTop: -5, marginRight: 10, marginLeft: -5 }} size='small'>
                                    <DocumentScannerIcon style={{ fontSize: 20 }} />
                                </IconButton>
                                <h3 style={{ marginTop: -1, fontWeight: 600 }}>Scan Purchase Invoice</h3>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        {/* Upload Area */}
                        <div className={`scan-upload-area ${dragActive ? 'drag-active' : ''} ${isScanning ? 'scanning' : ''}`}
                            onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                            onClick={() => !isScanning && fileInputRef.current?.click()}>
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf" style={{ display: 'none' }} />
                            {isScanning ? (
                                <Box>
                                    {previewImage ? (
                                        <div className="ocrloader-with-image" dangerouslySetInnerHTML={{ __html: `<p>Scanning</p><em></em><span></span>${previewImage.startsWith('data:application/pdf') ? `<iframe src="${previewImage}" style="width:100%;height:200px;border:none;border-radius:4px;opacity:0.7"></iframe>` : `<img src="${previewImage}" alt="Scanning" />`}` }} />
                                    ) : (
                                        <div className="ocrloader" dangerouslySetInnerHTML={{ __html: `<p>Scanning</p><em></em><span></span>` }} />
                                    )}
                                </Box>
                            ) : (
                                <Box>
                                    <div className="upload-icon-wrapper"><CloudUploadIcon sx={{ fontSize: 36, color: '#488dff' }} /></div>
                                    <Typography variant="h6" style={{ fontWeight: 600, color: '#333', marginTop: 8 }}>Drop invoice here or click to upload</Typography>
                                    <Typography variant="body2" style={{ color: '#9da7b7', marginTop: 4 }}>Supports PNG, JPG, WEBP, PDF (Max 10MB)</Typography>
                                </Box>
                            )}
                        </div>
                        {/* How it works (left) + Note (right) */}
                        <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1.5, backgroundColor: '#f0f7ff', borderRadius: '6px', border: '1px solid #d6e4ff', height: '100%' }}>
                                    <Typography variant="caption" sx={{ color: '#488dff', fontWeight: 600, display: 'block', mb: 0.5 }}>How it works:</Typography>
                                    <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1.8 }}>
                                        1. Upload invoice image or PDF above<br/>
                                        2. AI extracts Party, Bill To, Ship To, Items, Tax<br/>
                                        3. Review & edit extracted data if needed<br/>
                                        4. Click "Save Purchase Invoice" to create<br/>
                                        5. Image saved as document attachment
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #eee', height: '100%' }}>
                                    <Typography variant="caption" sx={{ color: '#488dff', fontWeight: 600, display: 'block', mb: 0.5 }}>Note:</Typography>
                                    <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1.8 }}>
                                        - If any data is missing or incorrect, you can edit it from the Purchase Invoice Edit Form after saving the scanned invoice.<br/>
                                        - Scanned image saved as <strong>Document Attachment</strong><br/>
                                        - Supplier auto-created if not found in system
                                        - Items auto-created if not found in system
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Card>
            )}

            {/* ==================== RESULT ==================== */}
            {scanResult && (
                <Card variant="outlined" className="scan-form-card" style={{ height: 'fit-content', border: '1px solid #d0d0d0', borderRadius: '8px', boxShadow: 'none' }}>
                    <Box sx={{ border: '3px solid #fff', backgroundColor: '#fff' }}>
                        <Grid container>
                            <Grid {...{ xs: 12, p: 2, pl: 1.5, pt: 1.6 }} style={{ height: 50 }}>
                                <div style={{ float: 'right', marginTop: -5, marginRight: -5 }}>
                                    <LoadingButton className='scan-btn-primary' size='small' onClick={() => handleSave('PENDING')} loading={isSaving} variant="contained">Save Purchase Invoice</LoadingButton>
                                    <LoadingButton className='scan-btn-secondary' size='small' onClick={() => navigate('/purchases')} variant="contained">Cancel</LoadingButton>
                                    <LoadingButton className='scan-btn-secondary' size='small' onClick={() => { setScanResult(null); setPreviewImage(''); }} variant="contained">Scan Again</LoadingButton>
                                </div>
                                <IconButton style={{ float: 'left', color: '#488dff', backgroundColor: '#e3f2fd', border: 'none', height: 35, width: 35, marginTop: -5, marginRight: 10, marginLeft: -5 }} size='small'>
                                    <DescriptionOutlinedIcon style={{ fontSize: 20 }} />
                                </IconButton>
                                <h3 style={{ marginTop: -1, fontWeight: 600 }}>New Purchase Invoice (Scanned)</h3>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Party + Bill To + Ship To - one row, equal size */}
                    <Grid container spacing={1.5} sx={{ p: 1.5, borderBottom: '1px dashed', borderColor: 'divider' }}>
                        {/* Party Card */}
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ p: 1.5, backgroundColor: '#f8faff', borderRadius: '8px', border: '1px solid #e3eaf5', height: '100%' }}>
                                <div className="scan-section-title" style={{ marginBottom: 6 }}>
                                    <StorefrontOutlinedIcon sx={{ fontSize: 16, color: '#488dff' }} /> Party
                                    {scanResult.isSupplierFound && <Chip icon={<CheckCircleIcon />} label="Found" color="success" size="small" sx={{ ml: 1, height: 20, fontSize: 10 }} />}
                                    {scanResult.isNewSupplier && <Chip icon={<WarningAmberIcon />} label="New" color="warning" size="small" sx={{ ml: 1, height: 20, fontSize: 10 }} />}
                                </div>
                                {scanResult.matchedSupplier ? (
                                    <>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', fontSize: 14 }}>{d?.supplierName || '-'}</Typography>
                                        {d?.supplierGST && <Typography variant="caption" display="block" sx={{ color: '#666' }}>GST: {d.supplierGST}</Typography>}
                                        {d?.supplierPAN && <Typography variant="caption" display="block" sx={{ color: '#666' }}>PAN: {d.supplierPAN}</Typography>}
                                        <Typography variant="caption" display="block" sx={{ color: '#666', mt: 0.3 }}>{getSupplierAddress()}</Typography>
                                        {d?.supplierPhone && <Typography variant="caption" display="block" sx={{ color: '#666' }}>Phone: {d.supplierPhone}</Typography>}
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', fontSize: 14 }}>{d?.supplierName}</Typography>
                                        {d?.supplierGST && <Typography variant="caption" display="block" sx={{ color: '#666' }}>GST: {d.supplierGST}</Typography>}
                                        {d?.supplierPAN && <Typography variant="caption" display="block" sx={{ color: '#666' }}>PAN: {d.supplierPAN}</Typography>}
                                        <Typography variant="caption" display="block" sx={{ color: '#666', mt: 0.3 }}>{getSupplierAddress()}</Typography>
                                        {d?.supplierPhone && <Typography variant="caption" display="block" sx={{ color: '#666' }}>Phone: {d.supplierPhone}</Typography>}
                                        <Typography variant="caption" display="block" sx={{ color: '#ea580c', mt: 0.5, fontWeight: 500 }}>New - Will be created on save</Typography>
                                    </>
                                )}
                            </Box>
                        </Grid>
                        {/* Bill To */}
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ p: 1.5, backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #eee', height: '100%' }}>
                                <div className="scan-section-title" style={{ marginBottom: 4, fontSize: 11 }}>
                                    <ReceiptLongOutlinedIcon sx={{ fontSize: 14, color: '#488dff' }} /> Bill To
                                </div>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{d?.billToName || '-'}</Typography>
                                <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1.4, mt: 0.3 }}>{getBillToAddress() || '-'}</Typography>
                                {(d?.billToGST) && <Typography variant="caption" display="block" sx={{ color: '#666' }}>GST: {d.billToGST}</Typography>}
                                {(d?.billToPAN) && <Typography variant="caption" display="block" sx={{ color: '#666' }}>PAN: {d.billToPAN}</Typography>}
                            </Box>
                        </Grid>
                        {/* Ship To */}
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ p: 1.5, backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #eee', height: '100%' }}>
                                <div className="scan-section-title" style={{ marginBottom: 4, fontSize: 11 }}>
                                    <LocalShippingOutlinedIcon sx={{ fontSize: 14, color: '#488dff' }} /> Ship To
                                </div>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{d?.shipToName || d?.billToName || '-'}</Typography>
                                <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1.4, mt: 0.3 }}>{getShipToAddress() || getBillToAddress() || '-'}</Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Grid container spacing={0}>
                        {/* ===== LEFT - Image + Bank ===== */}
                        <Grid item xs={12} sm={4} md={3.5} sx={{ borderRight: '1px dashed', borderColor: 'divider' }}>
                            <Box className="scan-preview-section" sx={{ p: 1.5 }}>
                                {/* Bank Details */}
                                {(d?.bankDetails?.bankName || d?.bankDetails?.accountNumber) && (
                                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #eee' }}>
                                        <div className="scan-section-title" style={{ marginBottom: 4, fontSize: 11 }}>
                                            <AccountBalanceOutlinedIcon sx={{ fontSize: 14, color: '#488dff' }} /> Bank Details
                                        </div>
                                        {d.bankDetails.bankName && <Typography variant="caption" display="block" sx={{ color: '#666' }}>Bank: {d.bankDetails.bankName}</Typography>}
                                        {d.bankDetails.accountNumber && <Typography variant="caption" display="block" sx={{ color: '#666' }}>A/C: {d.bankDetails.accountNumber}</Typography>}
                                        {d.bankDetails.ifsc && <Typography variant="caption" display="block" sx={{ color: '#666' }}>IFSC: {d.bankDetails.ifsc}</Typography>}
                                    </Box>
                                )}

                                {/* Image/PDF Preview */}
                                {previewImage && (
                                    <Box sx={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
                                        {previewImage.startsWith('data:application/pdf') ? (
                                            <iframe src={previewImage} style={{ width: '100%', height: '50vh', border: 'none' }} title="Invoice PDF" />
                                        ) : (
                                            <img src={previewImage} alt="Invoice" style={{ width: '100%', display: 'block' }} />
                                        )}
                                    </Box>
                                )}

                                {/* Note */}
                                <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fff8e1', borderRadius: '6px', border: '1px solid #ffe082' }}>
                                    <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 600, display: 'block', mb: 0.3 }}>Note:</Typography>
                                    <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.7 }}>
                                     If any data is missing or incorrect, you can edit it from the Purchase Invoice Edit Form after saving the scanned invoice.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* ===== RIGHT - Extracted Invoice Details ===== */}
                        <Grid item xs={12} sm={previewImage ? 8 : 12} md={previewImage ? 8.5 : 12}>
                            <Box sx={{ p: 2 }}>
                                <div className="scan-section-title">
                                    <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: '#488dff' }} /> Extracted Invoice Details
                                </div>
                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={6} sm={3}><label className='c-label' style={{ fontSize: 11 }}>Invoice Serial No.</label><input className='scan-input' value={invoiceSerialNumber} onChange={(e) => setInvoiceSerialNumber(e.target.value)} /></Grid>
                                    <Grid item xs={6} sm={3}><label className='c-label' style={{ fontSize: 11 }}>Delivery No.</label><input className='scan-input' value={deliveryNumber} onChange={(e) => setDeliveryNumber(e.target.value)} /></Grid>
                                    <Grid item xs={6} sm={3}><label className='c-label' style={{ fontSize: 11 }}>Invoice Date *</label><input type="date" className='scan-input' value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} /></Grid>
                                    <Grid item xs={6} sm={3}><label className='c-label' style={{ fontSize: 11 }}>Due Date</label><input type="date" className='scan-input' value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Grid>
                                    <Grid item xs={6} sm={3}><label className='c-label' style={{ fontSize: 11 }}>PO Number</label><input className='scan-input' value={poNumber} onChange={(e) => setPoNumber(e.target.value)} /></Grid>
                                    <Grid item xs={6} sm={3}>
                                        <label className='c-label' style={{ fontSize: 11 }}>Place of Supply</label>
                                        <Autocomplete size="small" options={stateOptions} getOptionLabel={(o: any) => o.label || ''} value={placeOfSupply}
                                            onChange={(_, v) => setPlaceOfSupply(v)} renderInput={(params) => <TextField {...params} placeholder="State" variant="outlined" size="small"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '5px', backgroundColor: '#e9ecf1', fontSize: 13, height: 32, border: 'none', '& fieldset': { border: 'none' } } }} />} />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <label className='c-label' style={{ fontSize: 11 }}>Tax Type</label>
                                        <select className='scan-input' style={{ height: 32 }} value={isTaxTypeIgst ? 'igst' : 'cgst_sgst'} onChange={(e) => setIsTaxTypeIgst(e.target.value === 'igst')}>
                                            <option value="cgst_sgst">CGST + SGST</option>
                                            <option value="igst">IGST</option>
                                        </select>
                                    </Grid>
                                </Grid>

                                {/* Line Items */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <div className="scan-section-title" style={{ margin: 0 }}>
                                        <InventoryOutlinedIcon sx={{ fontSize: 16, color: '#488dff' }} /> Line Items ({lineItems.length})
                                    </div>
                                    <Button size='small' className='scan-add-item-btn' variant='outlined' startIcon={<AddCircleOutlineOutlinedIcon />} onClick={addLineItem}>Add Item</Button>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ '& th': { backgroundColor: '#f5f7fa', color: '#666', border: '0.5px solid #e9ecf1', fontSize: 11, fontWeight: 700, py: 1, textTransform: 'uppercase', letterSpacing: 0.3 } }}>
                                                <TableCell width={30}>#</TableCell>
                                                <TableCell>Item / Description</TableCell>
                                                <TableCell width={95}>HSN/SAC</TableCell>
                                                <TableCell width={55} align="center">Qty</TableCell>
                                                <TableCell width={85} align="right">Rate</TableCell>
                                                <TableCell width={75} align="right">Discount</TableCell>
                                                <TableCell width={55} align="right">Tax%</TableCell>
                                                <TableCell width={95} align="right">Amount</TableCell>
                                                <TableCell width={70} align="center">Status</TableCell>
                                                <TableCell width={50} align="center">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {lineItems.map((item, index) => (
                                                <TableRow key={index} className="scan-table-row" sx={{ '& td': { border: '0.5px solid #e9ecf1', padding: '6px', verticalAlign: 'top' } }}>
                                                    <TableCell sx={{ color: '#999', fontSize: 12 }}>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <input className='scan-table-input item-name' value={item.name} onChange={(e) => updateLineItem(index, 'name', e.target.value)} />
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <input className='scan-table-input item-desc' value={item.description} onChange={(e) => updateLineItem(index, 'description', e.target.value)} placeholder="Description" />
                                                            {!item.matchedSku && <Chip label="New Item" color="warning" size="small" sx={{ fontSize: 9, height: 16, '& .MuiChip-label': { px: 0.5 } }} />}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell><input className='scan-table-input' value={item.hsnCode} onChange={(e) => updateLineItem(index, 'hsnCode', e.target.value)} /></TableCell>
                                                    <TableCell><input type="number" className='scan-table-input number-input' style={{ textAlign: 'center' }} value={item.qty} onChange={(e) => updateLineItem(index, 'qty', e.target.value)} /></TableCell>
                                                    <TableCell><input type="number" className='scan-table-input number-input' value={item.rate} onChange={(e) => updateLineItem(index, 'rate', e.target.value)} /></TableCell>
                                                    <TableCell><input type="number" className='scan-table-input number-input' value={item.discount} onChange={(e) => updateLineItem(index, 'discount', e.target.value)} /></TableCell>
                                                    <TableCell><input type="number" className='scan-table-input number-input' value={item.taxPercent} onChange={(e) => updateLineItem(index, 'taxPercent', e.target.value)} /></TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: 13, color: '#333' }}>₹ {Number(item.total || 0).toFixed(2)}</TableCell>
                                                    <TableCell align="center">
                                                        {item.matchedSku ? (
                                                            <Chip icon={<CheckCircleIcon />} label="Exists" color="success" size="small" sx={{ fontSize: 9, height: 20 }} />
                                                        ) : (
                                                            <Chip label="New" color="warning" size="small" sx={{ fontSize: 9, height: 20 }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton size="small" className="scan-delete-btn" onClick={() => removeLineItem(index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Notes */}
                                <Box sx={{ mt: 2 }}>
                                    <div className="scan-section-title"><NotesOutlinedIcon sx={{ fontSize: 16, color: '#488dff' }} /> Notes</div>
                                    <textarea className='scan-textarea' style={{ height: 40 }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" />
                                </Box>

                                {/* Totals */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <div className="scan-totals" style={{ width: 320 }}>
                                        <div className="scan-total-row"><span className="scan-total-label">Taxable Amount</span><span className="scan-total-value">₹ {taxableAmount.toFixed(2)}</span></div>
                                        {!isTaxTypeIgst && (<>
                                            <div className="scan-total-row"><span className="scan-total-label">CGST</span><span className="scan-total-value">₹ {cgstAmount.toFixed(2)}</span></div>
                                            <div className="scan-total-row"><span className="scan-total-label">SGST</span><span className="scan-total-value">₹ {sgstAmount.toFixed(2)}</span></div>
                                        </>)}
                                        {isTaxTypeIgst && <div className="scan-total-row"><span className="scan-total-label">IGST</span><span className="scan-total-value">₹ {igstAmount.toFixed(2)}</span></div>}
                                        <Divider sx={{ my: 1 }} />
                                        {additionalCharges.length > 0 && (
                                            <>
                                                {additionalCharges.map((charge, idx) => (
                                                    charge.title && Number(charge.value) > 0 ? (
                                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                                                            <span className="scan-total-label">{charge.title}</span>
                                                            <span className="scan-total-value">₹ {Number(charge.value).toFixed(2)}</span>
                                                        </div>
                                                    ) : null
                                                ))}
                                                <Divider sx={{ my: 1 }} />
                                            </>
                                        )}
                                        <div className="scan-total-row">
                                            <span className="scan-total-label" style={{ fontWeight: 500, fontSize: 13 }}>Total Amount</span>
                                            <span className="scan-total-value">₹ {totalAmount.toFixed(2)}</span>
                                        </div>
                                        {(Math.round(totalAmount) - totalAmount) !== 0 && (
                                            <div className="scan-total-row">
                                                <span className="scan-total-label" style={{ fontSize: 13 }}>Round Off</span>
                                                <span className="scan-total-value">₹ {(Math.round(totalAmount) - totalAmount).toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="scan-total-row">
                                            <span className="scan-total-label" style={{ fontWeight: 700, color: '#333', fontSize: 14 }}>Final Amount</span>
                                            <span className="scan-grand-total">₹ {Math.round(totalAmount).toFixed(2)}</span>
                                        </div>
                                    </div>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>
            )}
        </>
    );
};

export default ScanInvoice;
